import { TextField } from 'mui-rff';
import React, { useState } from 'react';

import CopyTextInput from '@dashboard/components/form/fields/CopyTextInput';
import Form from '@dashboard/components/form/Form';
import IconButtonLink from '@dashboard/components/mui-atoms/IconButtonLink';
import { useCreateApiKeyMutation } from '@dashboard/generated/wp/platform';
import useCurrentPlatformId from '@dashboard/hooks/route/useCurrentPlatformId';
import { ArrowBackOutlined } from '@mui/icons-material';
import { Box, Button, Grid, Paper, Stack, Typography } from '@mui/material';

import { alertError } from '@waypoint/react/notifications';

import PermissionTooltip from '../../components/PermissionTooltip';

import { usePermission } from './PlatformSettingsPage/hooks';

type TApiKey = { id: string; name: string; password: string };

export default function PlatformApiKeyNew() {
  const [mutation, { loading }] = useCreateApiKeyMutation();

  const permission = usePermission('createApiKey');
  const platformId = useCurrentPlatformId();

  const [apiKey, setApiKey] = useState<TApiKey | null>(null);
  const title = apiKey === null ? 'Create a new API key' : 'API key created';

  async function onSubmit(form: { name: string | undefined | null }) {
    try {
      const { data } = await mutation({ variables: { name: form.name || '' } });
      if (data) {
        setApiKey({
          id: data.createApiKey.id,
          name: data.createApiKey.name,
          password: data.createApiKey.password,
        });
      } else {
        alertError('There were errors creating your API key');
      }
    } catch (e) {
      alertError('There were errors creating your API key');
    }
  }

  const renderContent = () => {
    if (apiKey === null) {
      return (
        <Form onSubmit={onSubmit}>
          <TextField
            autoFocus
            disabled={loading}
            name="name"
            label="Label"
            helperText="A descriptive name for your API key."
            fullWidth
            margin="normal"
          />
          <Box mt={5}>
            <PermissionTooltip span name="createApiKey">
              <Button size="large" disabled={loading || !permission} type="submit" variant="contained">
                Create API key
              </Button>
            </PermissionTooltip>
          </Box>
        </Form>
      );
    }
    return <ResultApiKey apiKey={apiKey} />;
  };

  return (
    <Grid container justifyContent="center" sx={{ mt: 4 }}>
      <Grid item xs={12} md={10} lg={8} xl={7}>
        <Paper sx={{ p: 4 }}>
          <Stack spacing={2}>
            <Box>
              <IconButtonLink href={`/o/${platformId}/settings`} edge="start">
                <ArrowBackOutlined />
              </IconButtonLink>
            </Box>
            <Typography variant="h3" component="h1">
              {title}
            </Typography>
            {renderContent()}
          </Stack>
        </Paper>
      </Grid>
    </Grid>
  );
}

function ResultApiKey({ apiKey }: { apiKey: TApiKey }) {
  return (
    <Stack spacing={2} sx={{ width: '100%' }}>
      <Typography color="text.secondary" paragraph>
        Copy the information below and store it somewhere secure. For your security, the password is only shown once.
      </Typography>
      <CopyTextInput label="Name" value={apiKey.name} />
      <CopyTextInput label="Username" value={apiKey.id} />
      <CopyTextInput label="Password" value={apiKey.password} helperText="    This password won't be shown again." />
    </Stack>
  );
}
